import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LeaderboardIcon from '@mui/icons-material/Leaderboard'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { usersApi } from '@api/endpoints/users'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type { AdminAwardRankedResultDto, AdminRankedAwardCandidateDto } from '@api/types/checkInAward'

interface AwardRankedCompletionDialogProps {
  open: boolean
  onClose: () => void
  userId: string
  userLabel: string
}

function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`
}

/**
 * Grants a user a ranked completion with a time and steps an admin enters — for a run that happened
 * but the system could not verify, like a GPS track that never uploaded.
 *
 * When the user already has that held run, the award ranks it in place: one completion, keeping its
 * real start and finish. Otherwise it writes a new completion marked as admin-awarded. Either way it
 * ranks on the leaderboard, and the reason goes on the user's audit history.
 */
export default function AwardRankedCompletionDialog({ open, onClose, userId, userLabel }: AwardRankedCompletionDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [options, setOptions] = useState<AdminRankedAwardCandidateDto[]>([])
  const [searchText, setSearchText] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [selected, setSelected] = useState<AdminRankedAwardCandidateDto | null>(null)

  const [hours, setHours] = useState('')
  const [minutes, setMinutes] = useState('')
  const [seconds, setSeconds] = useState('')
  const [steps, setSteps] = useState('')
  const [completedAt, setCompletedAt] = useState<Date | null>(new Date())
  const [reason, setReason] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AdminAwardRankedResultDto | null>(null)

  // Keeps a slow response from overwriting the results of a later keystroke.
  const searchSeq = useRef(0)

  const runSearch = useCallback(
    async (term: string) => {
      const seq = ++searchSeq.current
      setSearching(true)
      try {
        const found = await usersApi.getRankedAwardCandidates(userId, term)
        if (seq === searchSeq.current) {
          setOptions(found)
          setSearchError(null)
        }
      } catch (err: unknown) {
        if (seq === searchSeq.current) {
          setOptions([])
          const status = (err as { response?: { status?: number } })?.response?.status
          setSearchError(status ? `Adventure search failed (HTTP ${status}).` : 'Adventure search failed — the request did not complete.')
        }
      } finally {
        if (seq === searchSeq.current) setSearching(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    if (!open) return
    const handle = setTimeout(() => runSearch(searchText), 300)
    return () => clearTimeout(handle)
  }, [open, searchText, runSearch])

  // Picking an adventure with a held run pre-fills what that run recorded, so the admin corrects
  // rather than retypes.
  const choose = (value: AdminRankedAwardCandidateDto | null) => {
    setSelected(value)
    const held = value?.heldRun
    const total = held?.durationSeconds ?? null
    setHours(total != null ? String(Math.floor(total / 3600)) : '')
    setMinutes(total != null ? String(Math.floor((total % 3600) / 60)) : '')
    setSeconds(total != null ? String(total % 60) : '')
    setSteps(held?.steps != null ? String(held.steps) : '')
  }

  const reset = () => {
    setSearchText('')
    setSearchError(null)
    choose(null)
    setCompletedAt(new Date())
    setReason('')
    setError(null)
    setResult(null)
  }

  const close = () => {
    // Reset on close so the next user does not inherit this one's entry.
    reset()
    onClose()
  }

  const durationSeconds =
    (parseInt(hours || '0', 10) || 0) * 3600 + (parseInt(minutes || '0', 10) || 0) * 60 + (parseInt(seconds || '0', 10) || 0)
  const stepCount = steps.trim() === '' ? undefined : parseInt(steps, 10)
  const heldRun = selected?.heldRun ?? null
  const inFuture = !heldRun && !!completedAt && completedAt.getTime() > Date.now()

  const canSubmit =
    !!selected &&
    selected.ranksCompletions &&
    durationSeconds > 0 &&
    (stepCount === undefined || (Number.isFinite(stepCount) && stepCount >= 0)) &&
    (heldRun || (!!completedAt && !inFuture)) &&
    reason.trim().length > 0 &&
    !submitting

  const handleAward = async () => {
    if (!selected) return
    setSubmitting(true)
    setError(null)
    try {
      const awarded = await usersApi.awardRanked(userId, {
        adventureId: selected.adventureId,
        durationSeconds,
        steps: stepCount,
        completedAt: heldRun ? undefined : completedAt?.toISOString(),
        reason: reason.trim(),
      })
      setResult(awarded)
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'The award could not be recorded.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Award a ranked completion</DialogTitle>

      {result ? (
        <>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>{result.rankedExistingRun ? 'Run ranked' : 'Ranked completion recorded'}</AlertTitle>
              {userLabel}&rsquo;s {result.rankedExistingRun ? 'run of' : 'completion of'} <strong>{result.adventureTitle}</strong>{' '}
              now ranks with a time of {formatDuration(result.durationSeconds)}
              {result.steps != null ? ` and ${result.steps.toLocaleString()} steps` : ''}.
            </Alert>
            {!result.earnedAchievementCredit && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No achievement credit applies, because {result.creditRefusedReason}.
              </Alert>
            )}
            {result.achievementsAdvanced.length > 0 && (
              <List dense>
                {result.achievementsAdvanced.map((a) => (
                  <ListItem key={a.achievementId} disableGutters>
                    <ListItemText
                      primary={a.achievementName}
                      secondary={`${a.collectionName} — ${a.completedBefore} → ${a.completedAfter} of ${a.required}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={close} variant="contained">
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Gives {userLabel} a ranked result on an adventure&rsquo;s leaderboard, with the time and steps you
              enter. Use it for a run that happened but could not be verified.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {searchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {searchError}
              </Alert>
            )}

            <Autocomplete
              sx={{ mb: 2 }}
              options={options}
              value={selected}
              loading={searching}
              onChange={(_, value) => choose(value)}
              onInputChange={(_, value, why) => {
                if (why === 'input') setSearchText(value)
              }}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(a, b) => a.adventureId === b.adventureId}
              filterOptions={(x) => x}
              noOptionsText={searching ? 'Searching…' : searchError ?? 'No adventures match'}
              renderOption={(props, option) => (
                <li {...props} key={option.adventureId}>
                  <Box sx={{ py: 0.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{option.title}</Typography>
                      {!option.ranksCompletions && <Chip size="small" label="Doesn't rank" />}
                      {option.heldRun && <Chip size="small" color="warning" label="Held run" />}
                    </Box>
                    {option.categoryName && (
                      <Typography variant="caption" color="text.secondary">
                        {option.categoryName}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Adventure"
                  placeholder="Search by title"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searching ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {selected && !selected.ranksCompletions && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This adventure doesn&rsquo;t rank completions, so there is no time to award. Use Award Check-In instead.
              </Alert>
            )}

            {heldRun && (
              <Alert severity="warning" icon={<LeaderboardIcon />} sx={{ mb: 2 }}>
                <AlertTitle>Ranks the run {userLabel} already finished</AlertTitle>
                Their run from{' '}
                {heldRun.completedAt ? new Date(heldRun.completedAt).toLocaleString() : 'an unknown date'} is held
                ({heldRun.verificationStatus.toLowerCase()}). This ranks that run with the time below and keeps its
                own start and finish; no second completion is added.
              </Alert>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Time
            </Typography>
            {/* Three equal columns with short labels and a numeric keypad: "Minutes"/"Seconds" got
                truncated in a third of a 360px dialog, and type=number alone opens a full keyboard. */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {(
                [
                  ['Hours', hours, setHours, undefined],
                  ['Min', minutes, setMinutes, 59],
                  ['Sec', seconds, setSeconds, 59],
                ] as const
              ).map(([label, value, setValue, max]) => (
                <TextField
                  key={label}
                  label={label}
                  type="number"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="0"
                  inputProps={{ min: 0, max, inputMode: 'numeric' }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              ))}
            </Stack>

            <TextField
              label="Steps"
              type="number"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              inputProps={{ min: 0 }}
              helperText="Optional"
              fullWidth
              sx={{ mb: 2 }}
            />

            {!heldRun && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="When the run finished"
                  value={completedAt}
                  onChange={setCompletedAt}
                  disableFuture
                  sx={{ width: '100%', mb: 2 }}
                  slotProps={{
                    textField: {
                      error: inFuture,
                      helperText: inFuture ? 'The run cannot finish in the future.' : ' ',
                    },
                  }}
                />
              </LocalizationProvider>
            )}

            <TextField
              label="Reason"
              placeholder="Why this is being ranked — recorded against the user's audit history"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleAward} variant="contained" disabled={!canSubmit}>
              {submitting ? 'Awarding…' : heldRun ? 'Rank this run' : 'Award ranked completion'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
